import config from 'constants/config'
import loadScript from 'lib/window/loadScript'

export const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js'

let readyPromise: Promise<void> | undefined
let initialized = false

export function isFacebookSdkReady(): boolean {
  return typeof window.FB?.login === 'function'
}

function initFacebookSdk() {
  if (initialized || !window.FB) {
    return
  }

  window.FB.init({
    appId: config.FACEBOOK_APP_ID,
    autoLogAppEvents: true,
    xfbml: false,
    version: 'v26.0',
  })
  initialized = true
}

export function ensureFacebookSdk(): Promise<void> {
  if (isFacebookSdkReady()) {
    initFacebookSdk()
    return Promise.resolve()
  }

  if (readyPromise) {
    return readyPromise
  }

  readyPromise = new Promise<void>((resolve, reject) => {
    let settled = false

    const finish = () => {
      initFacebookSdk()
      if (settled || !isFacebookSdkReady()) {
        return
      }
      settled = true
      resolve()
    }

    window.fbAsyncInit = finish

    loadScript(FACEBOOK_SDK_SRC, { async: true, defer: true, crossOrigin: 'anonymous' }).then(() => {
      // The script load event can fire before `window.FB` is assigned / fbAsyncInit
      // runs. finish() is the source of truth; do not reject that race.
      finish()
    }).catch((error) => {
      readyPromise = undefined
      reject(error)
    })
  })

  return readyPromise
}

export function preloadFacebookSdk() {
  if (!config.FACEBOOK_APP_ID) {
    return
  }

  ensureFacebookSdk().catch(() => {})
}

export function resetFacebookSdk() {
  readyPromise = undefined
  initialized = false
}
